/* LALEY cart recovery v1. Keeps native Klaviyo events and flow triggers unchanged. */
(() => {
  'use strict';
  if (window.__laleyCartRecovery) return;
  const state = window.__laleyCartRecovery = { version: '20260912-v1' };
  let generation = 0;
  let timer;
  let fingerprint = null;
  let committedAt = 0;
  const permitted = () => {
    try { return window.Shopify?.customerPrivacy?.marketingAllowed() === true; }
    catch (_) { return false; }
  };
  const bounded = (promise) => Promise.race([
    Promise.resolve(promise),
    new Promise((_, reject) => window.setTimeout(() => reject(new Error('timeout')), 4000))
  ]);
  function snapshot(cart) {
    if (!cart || !Array.isArray(cart.items)) throw new Error('Invalid cart response');
    const variants = new Map();
    const products = new Set();
    let compatible = cart.items.length <= 30;
    for (const item of cart.items) {
      const variant = String(item.variant_id || '');
      const product = String(item.product_id || '');
      const quantity = Number(item.quantity);
      // Permalinks cannot faithfully carry subscriptions, bundles or custom line properties.
      if (!/^\d+$/.test(variant) || !/^\d+$/.test(product) || !Number.isSafeInteger(quantity) || quantity < 1 || quantity > 100
        || item.selling_plan_allocation || item.parent_relationship || item.item_components?.length
        || Object.keys(item.properties || {}).length) { compatible = false; break; }
      variants.set(variant, (variants.get(variant) || 0) + quantity);
      products.add(product);
    }
    const items = compatible ? [...variants].map(([id, qty]) => `${id}:${qty}`).join(',') : '';
    return {
      laley_cart_restore_items: items || 'none',
      laley_cart_restore_ready: Boolean(items),
      laley_cart_restore_product_ids: compatible ? [...products] : [],
      laley_cart_restore_version: 'v1',
    };
  }
  async function refresh(epoch) {
    if (!permitted()) { fingerprint = null; return; }
    const kl = window.klaviyo;
    if (typeof kl?.isIdentified !== 'function' || typeof kl?.identify !== 'function') return;
    try {
      if (await bounded(kl.isIdentified()) !== true || !permitted() || epoch !== generation) return;
      const controller = new AbortController();
      const deadline = window.setTimeout(() => controller.abort(), 5000);
      let cart;
      try {
        const root = window.Shopify?.routes?.root || '/';
        const response = await fetch(`${root}cart.js`, { credentials: 'same-origin', cache: 'no-store', signal: controller.signal });
        if (!response.ok) throw new Error('Cart unavailable');
        cart = await response.json();
      } finally { window.clearTimeout(deadline); }
      const properties = snapshot(cart);
      const nextFingerprint = JSON.stringify(properties);
      if (epoch !== generation || !permitted()) return;
      // Refresh the timestamp once per minute even when the same cart is revisited.
      if (fingerprint === nextFingerprint && Date.now() - committedAt < 60000) return;
      if (await bounded(kl.isIdentified()) !== true || !permitted() || epoch !== generation) return;
      properties.laley_cart_restore_at = new Date().toISOString().replace(/\.\d{3}Z$/, 'Z');
      await bounded(kl.identify(properties));
      if (epoch === generation) {
        fingerprint = nextFingerprint;
        committedAt = Date.now();
        state.lastSync = properties.laley_cart_restore_at;
      }
    } catch (_) {
      // Recovery must never interfere with adding products, consent or checkout.
    }
  }
  function schedule() {
    const epoch = ++generation;
    window.clearTimeout(timer);
    timer = window.setTimeout(() => refresh(epoch), 450);
  }
  ['theme:cart:change', 'theme:cart:updated', 'cart:updated', 'cart:change', 'theme:cart:refresh', 'theme:product:added', 'visitorConsentCollected', 'klaviyoForms'].forEach(name => document.addEventListener(name, schedule));
  window.addEventListener('pageshow', schedule);
  window.addEventListener('focus', schedule);
  [0, 2000, 6000, 15000].forEach(delay => window.setTimeout(schedule, delay));
})();
